import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Select from 'react-select';
// import * as XLSX from "xlsx";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { SITE_TITLE } from '../../utils/constants';
import { seo } from '../../utils/seo';
import useAuth from '../../hooks/useAuth';
import './TerritoryMultiplier.css';
import GearSpinner from '../../components/GearSpinner/GearSpinner';
import Loader from '../../components/Loader/Loader';
import TerritoryMultiplierServices from '../../services/TerritoryMultiplierService';
import AlertMessage from '../../components/AlertMessage/AlertMessage';
import ascIcon from '../../assets/images/sorting-asc.png';
import descIcon from '../../assets/images/sorting-desc.png';
import defaultIcon from '../../assets/images/sorting-default.png';
import xlsIcon from '../../assets/images/xls-img.png';

const territoryMultiplierServices = new TerritoryMultiplierServices();

const TerritoryMultiplierReportPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [showLoader, setShowLoader] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [multiplierData, setMultipliersData] = useState([]);
    const [sortField, setSortField] = useState('customer_name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [sortedData, setSortedData] = useState([]);
    const [selectedField, setSelectedField] = useState('customer_name');
    const [selectedFieldValue, setSelectedFieldValue] = useState('ALL');
    const [tableHeaders, setTableHeaders] = useState([
        { label: 'Customer', field: 'customer_name', sorting_icon: ascIcon, is_sortable: true},
        { label: 'Address', field: 'address', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'City', field: 'city', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'State', field: 'state', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'Zip', field: 'zip_code', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'G1', field: 'multipliers.G1', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'AG', field: 'multipliers.AG', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'AE', field: 'multipliers.AE', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'VS', field: 'multipliers.VS', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'R1', field: 'multipliers.R1', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'CB', field: 'multipliers.CB', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'IE', field: 'multipliers.IE', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'P1', field: 'multipliers.P1', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'PT', field: 'multipliers.PT', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'PC', field: 'multipliers.PC', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'B1', field: 'multipliers.B1', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'CP', field: 'multipliers.CP', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'DC', field: 'multipliers.DC', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'CT', field: 'multipliers.CT', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'W1', field: 'multipliers.W1', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'SS', field: 'multipliers.SS', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'X1', field: 'multipliers.X1', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'SD', field: 'multipliers.SD', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'BK', field: 'multipliers.BK', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'O1', field: 'multipliers.O1', sorting_icon: defaultIcon, is_sortable: true},
        { label: 'V1', field: 'multipliers.V1', sorting_icon: defaultIcon, is_sortable: true}
    ]);
    
    useEffect(() => {
        seo({
            title: SITE_TITLE + ' || Territory Multiplier Report',
            metaDescription: 'Territory Multiplier Report Page',
        });
    }, []);

    useEffect(() => {
        fetchMultiplierData();
    }, []);

    useEffect(() => {
        if(loading && sortedData.length > 0 && selectedFieldValue === "ALL") {
            setLoading(false);
        }
    }, [sortedData]);
    

    useEffect(() => {
        if (selectedFieldValue === "ALL" || !selectedFieldValue) {
            setSortedData(multiplierData);
            return;
        }

        const filtered = multiplierData.filter(item => {
            const fieldValue = getNestedValue(item, selectedField);
            return String(fieldValue) === String(selectedFieldValue);
        });

        setSortedData(filtered);
    }, [selectedField, selectedFieldValue, multiplierData]);

    const fetchMultiplierData = () => {
        setLoading(true);

        territoryMultiplierServices
            .getTerritoryMultiplierData({ portal_user_name: user?.name })
            .then((response) => {
                if (response.data.status === 'success') {
                    setMultipliersData(response.data.result);
                    setSortedData(response.data.result);
                } else {
                    setMultipliersData([]);
                    setSortedData([]);
                    setAlert({ type: 'danger', message: 'Failed to fetch multiplier data.' });
                }
            })
            .catch(() => {
                setMultipliersData([]);
                setSortedData([]);
                setAlert({ type: 'danger', message: 'Failed to fetch multiplier data.' });
            })
            .finally(() => {
                setLoading(false); // ✔ loader hides **after** data arrives
            });
    };

    const handleSorting = (field) => {
        setLoading(true);
        let order = 'asc';
        if (sortField === field && sortOrder === 'asc') {
            order = 'desc';
        }

        // Update sorting icons
        const updatedHeaders = tableHeaders.map(header => {
            if (header.field === field) {
                return { ...header, sorting_icon: order === 'asc' ? ascIcon : descIcon };
            } else {
                return { ...header, sorting_icon: defaultIcon };
            }
        });
        setTableHeaders(updatedHeaders);
        setSortField(field);
        setSortOrder(order);
        sortData(field, order);
    }

    const sortData = (field, order = "asc") => {
        // Copy array instead of object
        const sorted = [...sortedData];

        // Utility for nested fields (multipliers.AG)
        const getValue = (obj, path) => {
            return path.split('.').reduce((acc, key) => acc?.[key], obj) ?? '';
        };

        const sortedArray = sorted.sort((a, b) => {
            const valA = getValue(a, field);
            const valB = getValue(b, field);

            const numA = parseFloat(valA);
            const numB = parseFloat(valB);

            const isNumber = !isNaN(numA) && !isNaN(numB);

            if (isNumber) {
                return order === "asc" ? numA - numB : numB - numA;
            } else {
                return order === "asc"
                    ? String(valA).localeCompare(String(valB))
                    : String(valB).localeCompare(String(valA));
            }
        });

        setSortedData(sortedArray);
        setLoading(false);
    };

    const handleFieldChange = (selectedOption) => {
        const field = selectedOption?.value || "";

        setSelectedField(field);          // Update main field
        setSelectedFieldValue("ALL");     // Reset second dropdown
        setSortedData([]);                // Clear table
        setLoading(true);                 // Trigger loader
    };

    const handleFieldValueChange = (selectedOption) => {
        const value = selectedOption?.value || "ALL";
        setSelectedFieldValue(value);     // Update selected value
    };

    const downloadExcel = () => {
        if (!sortedData || sortedData.length === 0) {
            alert("No data available to export");
            return;
        }

        setShowLoader(true);

        // Convert dynamic headers
        const headerMap = tableHeaders.map(h => ({
            label: h.label,
            field: h.field
        }));

        // Map rows according to visible sorted + filtered data
        const exportRows = sortedData.map(row => {
            const obj = {};

            headerMap.forEach(col => {
                obj[col.label] = getNestedValue(row, col.field);
            });

            return obj;
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(exportRows);

        // -------------------------
        // 1️⃣ Set Header Background Color (Blue) + Bold
        // -------------------------
        const headerRange = XLSX.utils.decode_range(ws['!ref']);

        for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
            const cell = ws[cellAddress];

            if (cell) {
                cell.s = {
                    fill: {
                        fgColor: { rgb: "4A90E2" } // Blue color
                    },
                    font: {
                        bold: true,
                        color: { rgb: "FFFFFF" } // White text
                    }
                };
            }
        }

        // -------------------------
        // 2️⃣ AutoFilter (Excel header dropdown filters)
        // -------------------------
        ws['!autofilter'] = {
            ref: ws['!ref']  // Apply filter to full header range
        };

        // -------------------------
        // 3️⃣ Auto column width
        // -------------------------
        const colWidths = headerMap.map(col => ({
            wch: Math.max(
                col.label.length,
                ...sortedData.map(row =>
                    String(getNestedValue(row, col.field) ?? "").length
                )
            ) + 2
        }));

        ws['!cols'] = colWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Multiplier Report");

        // Export
        const excelBuffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array",
            cellStyles: true // <-- enable styles
        });

        const formattedDate = moment().format('MM/DD/YYYY HH:mm');
        const fileName = `${user?.name}-territory-multiplier-report-${formattedDate}.xlsx`;

        saveAs(
            new Blob([excelBuffer], { type: "application/octet-stream" }),
            fileName
        );
        setTimeout(() => { setShowLoader(false); }, 500);
        
    };



    const renderSearchSection = () => {
        const fieldOptions = tableHeaders
            .filter(header => header.is_sortable)
            .map(header => ({ value: header.field, label: header.label }));
        
        const values = [...new Set(
            multiplierData.map(item => getNestedValue(item, selectedField))
        )].filter(v => v !== "" && v !== null && v !== undefined);

        const sortedValues = values
            .map(v => String(v))
            .sort((a, b) => a.localeCompare(b));

        const fieldValueOptions = [
            { value: "ALL", label: "All" },   // ✓ All option added
            ...sortedValues.map(v => ({ value: v, label: v }))
        ];

        return (
            <div className="row mt-2 d-flex justify-content-center">
                <div className="col-md-4 d-flex justify-content-center">
                    <div className="w-100">
                        <Select
                            options={fieldOptions}
                            value={fieldOptions.find(option => option.value === selectedField) || null}
                            onChange={handleFieldChange}
                            placeholder="Select Field"
                        />
                    </div>
                </div>

                <div className="col-md-4 d-flex justify-content-center">
                    <div className="w-100">
                        <Select
                            key={selectedField}  
                            options={fieldValueOptions}
                            value={fieldValueOptions.find(option => option.value === selectedFieldValue) || null}
                            onChange={handleFieldValueChange}
                            placeholder="Select Value"
                            isSearchable={true}
                        />

                    </div>
                </div>
                <div className="col-md-4 d-flex justify-content-center">
                    <div className="w-100">
                        <button className="btn-link-style" onClick={downloadExcel}><img src={xlsIcon} alt="Export Excel" />Export Excel</button>
                    </div>
                </div>
            </div>
        );
    };

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, key) => acc?.[key], obj) ?? '';
    };

    const renderMultiplierTable = () => {
        return (
            <div className="col-md-12 mt-2">
                <h3 className="dark-color1 text-center mb-1">Multiplier by Customer for {user?.name}</h3>
                <div className="table-container">
                    <table className="table table-bordered text-center" id="techtop-territory-multiplier-table">
                        <thead>
                            <tr>
                                {tableHeaders.map((header, index) => (
                                    <th key={index}>
                                        {header.label} {header.is_sortable && (<img src={header.sorting_icon} alt="sorting_icon" className="sorting-icon" onClick={() => handleSorting(`${header.field}`)}/>)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan="24" className="text-center">
                                        <GearSpinner />
                                    </td>
                                </tr>
                            )}

                            {!loading && sortedData.length === 0 && (
                                <tr>
                                    <td colSpan="24"><h6>No data available</h6></td>
                                </tr>
                            )}

                            {!loading && sortedData.length > 0 &&
                                sortedData.map((customer, index) => (
                                    <tr key={index}>
                                        <td>{customer.customer_name}</td>
                                        <td>{customer.address}</td>
                                        <td>{customer.city}</td>
                                        <td>{customer.state}</td>
                                        <td>{customer.zip_code}</td>

                                        <td>{customer.multipliers.G1 || ""}</td>
                                        <td>{customer.multipliers.AG || ""}</td>
                                        <td>{customer.multipliers.AE || ""}</td>
                                        <td>{customer.multipliers.VS || ""}</td>
                                        <td>{customer.multipliers.R1 || ""}</td>
                                        <td>{customer.multipliers.CB || ""}</td>
                                        <td>{customer.multipliers.IE || ""}</td>
                                        <td>{customer.multipliers.P1 || ""}</td>
                                        <td>{customer.multipliers.PT || ""}</td>
                                        <td>{customer.multipliers.PC || ""}</td>
                                        <td>{customer.multipliers.B1 || ""}</td>
                                        <td>{customer.multipliers.CP || ""}</td>
                                        <td>{customer.multipliers.DC || ""}</td>
                                        <td>{customer.multipliers.CT || ""}</td>
                                        <td>{customer.multipliers.W1 || ""}</td>
                                        <td>{customer.multipliers.SS || ""}</td>
                                        <td>{customer.multipliers.X1 || ""}</td>
                                        <td>{customer.multipliers.SD || ""}</td>
                                        <td>{customer.multipliers.BK || ""}</td>
                                        <td>{customer.multipliers.O1 || ""}</td>
                                        <td>{customer.multipliers.V1 || ""}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <main className="techtop-middle-div">
                <section className="techtop-multipler-schedule-form-section pb-0">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="techtop-default-title2">
                                    <div className="row">
                                        <div className="col-6">
                                            <h1 className="m-0 dark-color1">Territory Multiplier Report</h1>
                                        </div>
                                        <div className="col-6">
                                            <h2 className="m-0 dark-color1 text-end">{user?.name}</h2>
                                        </div>
                                    </div>
                                </div>
                                <AlertMessage
                                    type={alert.type}
                                    message={alert.message}
                                    onClose={() => setAlert({ type: '', message: '' })}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                {renderSearchSection()}
                            </div>
                        </div>
                    </div>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12">
                                {renderMultiplierTable()}
                            </div>
                        </div>
                    </div>
                    <Loader isLoading={showLoader} message="Processing..."/>
                </section>
            </main>
        </React.Fragment>
    );

}

export default TerritoryMultiplierReportPage;